"use client";

import { Check } from "@icon-park/svg";
import clsx from "clsx";
import {
  ChangeEventHandler,
  FocusEventHandler,
  PropsWithChildren,
  forwardRef,
} from "react";
import Icon from "../Icon";

export interface RadioCardProps {
  name?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: ChangeEventHandler;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  "aria-invalid"?: boolean;
  type: "checkbox" | "radio";
  className?: string;
  value: string;
  icon?: React.ReactNode;
  size?: "xs" | "sm" | "md";
  disabled?: boolean;
}

const RadioCard = forwardRef<
  HTMLInputElement,
  PropsWithChildren<RadioCardProps>
>(function RadioCard(
  {
    name,
    value,
    type,
    size = "md",
    defaultChecked,
    checked,
    onChange,
    onBlur,
    "aria-invalid": ariaInvalid,
    className,
    children,
    disabled,
    icon,
  },
  ref,
) {
  const isCheckbox = type === "checkbox";

  return (
    <label
      data-testid={`${name}-${value}`.toLocaleLowerCase()}
      className={clsx(
        "flex w-full text-app-body-2 relative group/switch",
        className,
        {
          "cursor-pointer": !disabled,
          "cursor-not-allowed": disabled,
        },
      )}
    >
      <input
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={ariaInvalid}
        name={name}
        value={value}
        ref={ref}
        type={type}
        defaultChecked={defaultChecked}
        checked={checked}
        className="h-none w-none absolute overflow-hidden opacity-0 peer"
      />
      <span
        className={clsx(
          "bg-neutral-0 w-2s h-2s border border-neutral-150 peer-checked:hidden absolute top-1/2 -translate-y-1/2",
          isCheckbox ? "rounded-xs" : "rounded-full",
          {
            "left-3s w-2s h-2s": !icon && size === "md",
            "left-s w-2s h-2s": !icon && size === "sm",
            "left-3xs w-s h-s": !icon && size === "xs",
            "right-3s w-2s h-2s": icon && size === "md",
            "right-s w-2s h-2s": icon && size === "sm",
            "right-3xs w-s h-s": icon && size === "xs",
            "group-hover/switch:border-primary-800 group-active/switch:border-primary-875":
              !disabled,
          },
        )}
      />
      <span
        className={clsx(
          "hidden w-2s h-2s border border-current peer-checked:block absolute top-1/2 -translate-y-1/2",
          isCheckbox ? "rounded-xs bg-current" : "rounded-full bg-neutral-0",
          {
            "left-3s w-2s h-2s": !icon && size === "md",
            "left-s w-2s h-2s": !icon && size === "sm",
            "left-3xs w-s h-s": !icon && size === "xs",
            "right-3s w-2s h-2s": icon && size === "md",
            "right-s w-2s h-2s": icon && size === "sm",
            "right-3xs w-s h-s": icon && size === "xs",
            "text-primary-800": !disabled,
            "text-neutral-150": disabled,
          },
        )}
      >
        {isCheckbox ? (
          <span className="flex h-full w-full items-center justify-center text-neutral-0">
            <Icon icon={Check} size={size === "xs" ? 12 : 14} />
          </span>
        ) : (
          <span className="block rounded-full w-full h-full bg-current border-[3px] border-neutral-0"></span>
        )}
      </span>
      <span
        className={clsx(
          "bg-neutral-0 w-full  border border-neutral-50 rounded-s peer-focus-visible:ring-2 ring-primary-800 ring-offset-2  flex gap-s items-center peer-checked:border-primary-800 hover:bg-neutral-12 transition-colors peer-checked:shadow-4 peer-checked:bg-primary-25",
          {
            "pl-xl pr-3s py-[11px]": !icon && size === "md",
            "pl-3m pr-2s py-[11px]": !icon && size === "sm",
            "pl-2m pr-3xs py-[7px]": !icon && size === "xs",
            "pr-xl pl-3s py-[11px]": icon && size === "md",
            "pr-3m pl-2s py-[11px]": icon && size === "sm",
            "pr-2m pl-3xs py-[7px]": icon && size === "xs",
            "group-hover/switch:border-primary-800 group-active/switch:border-primary-875 peer-checked/switch:border-primary-800":
              !disabled,
            "bg-neutral-12": disabled,
          },
        )}
      >
        {icon && (
          <span
            className={clsx(
              "flex-none flex items-center justify-center text-neutral-675",
              {
                "h-3s w-3s": size === "sm",
                "h-2m w-2m": size === "md",
                "h-s w-s": size === "xs",
              },
            )}
          >
            {icon}
          </span>
        )}
        <span
          className={clsx("flex-1 text-neutral-675 max-w-full", {
            "text-body-1": size === "md",
            "text-app-body-1": size === "sm",
            "text-app-body-2": size === "xs",
          })}
        >
          {children}
        </span>
      </span>
    </label>
  );
});

export default RadioCard;
